// Compiles untrusted dashboard TSX with @babel/standalone, then evaluates
// it inside a controlled scope of shadcn UI components, React hooks, Recharts
// primitives, and the lucide + phosphor icon sets. Moved verbatim out of the
// in-app LiveRender so this code now runs in the sandbox origin instead of the
// main app's page — see apps/dashboard-sandbox/README.md.
//
// The shadcn primitives are deep-imported from the ontology UI tree to
// keep a single source of truth; there is no second shadcn fork.
//
// **Keep this file paired with
// `packages/ontology/agents/src/agents/dashboard/shadcn-docs.ts`.**
// Every component documented over there must be importable + spread into SCOPE
// here, or the dashboard-builder LLM will reference a name that won't render.

import * as ReactNamespace from 'react'
import type { ComponentType } from 'react'
import * as LucideIcons from 'lucide-react'
import * as PhosphorIcons from '@phosphor-icons/react'
import * as Recharts from 'recharts'
import { usesUtcIsoDashboardTimestamps } from '@atbash-labs/ontology-agent-wire'
import {
  dashboardHref,
  dashboardTemplateHref,
  docsHubDocumentHref,
  plantHref,
  tagsTagHref
} from '@atbash-labs/plantview-links'

import { ReviewActions } from './components/review-actions'
import { setTemplateCompiler, Template } from './components/template'

import {
  useActiveAlarms,
  useAgentFindings,
  useAgentResults,
  useAlarmStatus,
  useCypherQuery,
  useDbConnections,
  useDbQuery,
  useDbWrite,
  useGatewayStatus,
  useLegacyTagHistory,
  usePowerAnalysis,
  useSoapOperation,
  useTagHistory,
  useTagWrite,
  useTagValues
} from './data-runtime'

// P&ID / process-mimic primitives owned by the dashboard sandbox.
import {
  AnalogIndicator,
  At,
  PidDiagram,
  PidNode,
  Pipe,
  PipePath,
  Pump,
  Sensor,
  Tank,
  Thermometer,
  U,
  Valve
} from './components/pid'

// Deep imports into the ontology UI tree are intentional — see this
// app's eslint.config.mjs and README for rationale. Sorted alphabetically
// by component file to make additions easy to review.
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '../../../domains/ontology-ui/main/src/components/ui/accordion'
import {
  Alert,
  AlertDescription,
  AlertTitle
} from '../../../domains/ontology-ui/main/src/components/ui/alert'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertDialogPortal,
  AlertDialogTitle,
  AlertDialogTrigger
} from '../../../domains/ontology-ui/main/src/components/ui/alert-dialog'
import { AspectRatio } from '../../../domains/ontology-ui/main/src/components/ui/aspect-ratio'
import {
  Avatar,
  AvatarBadge,
  AvatarFallback,
  AvatarGroup,
  AvatarGroupCount,
  AvatarImage
} from '../../../domains/ontology-ui/main/src/components/ui/avatar'
import { Badge } from '../../../domains/ontology-ui/main/src/components/ui/badge'
import {
  Breadcrumb,
  BreadcrumbEllipsis,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator
} from '../../../domains/ontology-ui/main/src/components/ui/breadcrumb'
import { Button } from '../../../domains/ontology-ui/main/src/components/ui/button'
import {
  ButtonGroup,
  ButtonGroupSeparator,
  ButtonGroupText
} from '../../../domains/ontology-ui/main/src/components/ui/button-group'
import {
  Calendar,
  CalendarDayButton
} from '../../../domains/ontology-ui/main/src/components/ui/calendar'
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle
} from '../../../domains/ontology-ui/main/src/components/ui/card'
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious
} from '../../../domains/ontology-ui/main/src/components/ui/carousel'
import {
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartStyle,
  ChartTooltip,
  ChartTooltipContent
} from '../../../domains/ontology-ui/main/src/components/ui/chart'
import { Checkbox } from '../../../domains/ontology-ui/main/src/components/ui/checkbox'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '../../../domains/ontology-ui/main/src/components/ui/collapsible'
import {
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut
} from '../../../domains/ontology-ui/main/src/components/ui/command'
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuLabel,
  ContextMenuSeparator,
  ContextMenuTrigger
} from '../../../domains/ontology-ui/main/src/components/ui/context-menu'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger
} from '../../../domains/ontology-ui/main/src/components/ui/dialog'
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerPortal,
  DrawerTitle,
  DrawerTrigger
} from '../../../domains/ontology-ui/main/src/components/ui/drawer'
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger
} from '../../../domains/ontology-ui/main/src/components/ui/dropdown-menu'
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle
} from '../../../domains/ontology-ui/main/src/components/ui/empty'
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSeparator,
  FieldSet,
  FieldTitle
} from '../../../domains/ontology-ui/main/src/components/ui/field'
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger
} from '../../../domains/ontology-ui/main/src/components/ui/hover-card'
import { Input } from '../../../domains/ontology-ui/main/src/components/ui/input'
import {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
  InputGroupText,
  InputGroupTextarea
} from '../../../domains/ontology-ui/main/src/components/ui/input-group'
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot
} from '../../../domains/ontology-ui/main/src/components/ui/input-otp'
import {
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemFooter,
  ItemGroup,
  ItemHeader,
  ItemMedia,
  ItemSeparator,
  ItemTitle
} from '../../../domains/ontology-ui/main/src/components/ui/item'
import { Kbd, KbdGroup } from '../../../domains/ontology-ui/main/src/components/ui/kbd'
import { Label } from '../../../domains/ontology-ui/main/src/components/ui/label'
import {
  Menubar,
  MenubarCheckboxItem,
  MenubarContent,
  MenubarGroup,
  MenubarItem,
  MenubarLabel,
  MenubarMenu,
  MenubarPortal,
  MenubarRadioGroup,
  MenubarRadioItem,
  MenubarSeparator,
  MenubarShortcut,
  MenubarSub,
  MenubarSubContent,
  MenubarSubTrigger,
  MenubarTrigger
} from '../../../domains/ontology-ui/main/src/components/ui/menubar'
import {
  NativeSelect,
  NativeSelectOptGroup,
  NativeSelectOption
} from '../../../domains/ontology-ui/main/src/components/ui/native-select'
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuIndicator,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  NavigationMenuViewport
} from '../../../domains/ontology-ui/main/src/components/ui/navigation-menu'
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious
} from '../../../domains/ontology-ui/main/src/components/ui/pagination'
import {
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverDescription,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger
} from '../../../domains/ontology-ui/main/src/components/ui/popover'
import { Progress } from '../../../domains/ontology-ui/main/src/components/ui/progress'
import {
  RadioGroup,
  RadioGroupItem
} from '../../../domains/ontology-ui/main/src/components/ui/radio-group'
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup
} from '../../../domains/ontology-ui/main/src/components/ui/resizable'
import {
  ScrollArea,
  ScrollBar
} from '../../../domains/ontology-ui/main/src/components/ui/scroll-area'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue
} from '../../../domains/ontology-ui/main/src/components/ui/select'
import { Separator } from '../../../domains/ontology-ui/main/src/components/ui/separator'
import {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger
} from '../../../domains/ontology-ui/main/src/components/ui/sheet'
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupAction,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInput,
  SidebarInset,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarRail,
  SidebarSeparator,
  SidebarTrigger
} from '../../../domains/ontology-ui/main/src/components/ui/sidebar'
import { Skeleton } from '../../../domains/ontology-ui/main/src/components/ui/skeleton'
import { Slider } from '../../../domains/ontology-ui/main/src/components/ui/slider'
import { Toaster } from '../../../domains/ontology-ui/main/src/components/ui/sonner'
import { Spinner } from '../../../domains/ontology-ui/main/src/components/ui/spinner'
import { Switch } from '../../../domains/ontology-ui/main/src/components/ui/switch'
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow
} from '../../../domains/ontology-ui/main/src/components/ui/table'
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger
} from '../../../domains/ontology-ui/main/src/components/ui/tabs'
import { Textarea } from '../../../domains/ontology-ui/main/src/components/ui/textarea'
import { Toggle } from '../../../domains/ontology-ui/main/src/components/ui/toggle'
import {
  ToggleGroup,
  ToggleGroupItem
} from '../../../domains/ontology-ui/main/src/components/ui/toggle-group'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '../../../domains/ontology-ui/main/src/components/ui/tooltip'

// Recharts primitives exposed inside ChartContainer. Keeping the explicit
// list (rather than spreading `Recharts.*` blindly) means new dashboard
// authors get the SAME set the system prompt documents — adding a new
// Recharts primitive is a deliberate, reviewed change.
const RECHARTS_SCOPE = {
  AreaChart: Recharts.AreaChart,
  BarChart: Recharts.BarChart,
  ComposedChart: Recharts.ComposedChart,
  LineChart: Recharts.LineChart,
  PieChart: Recharts.PieChart,
  RadarChart: Recharts.RadarChart,
  RadialBarChart: Recharts.RadialBarChart,
  ScatterChart: Recharts.ScatterChart,
  Area: Recharts.Area,
  Bar: Recharts.Bar,
  Cell: Recharts.Cell,
  Line: Recharts.Line,
  Pie: Recharts.Pie,
  PolarAngleAxis: Recharts.PolarAngleAxis,
  PolarGrid: Recharts.PolarGrid,
  PolarRadiusAxis: Recharts.PolarRadiusAxis,
  Radar: Recharts.Radar,
  RadialBar: Recharts.RadialBar,
  Scatter: Recharts.Scatter,
  XAxis: Recharts.XAxis,
  YAxis: Recharts.YAxis,
  ZAxis: Recharts.ZAxis,
  CartesianGrid: Recharts.CartesianGrid,
  // `Tooltip` is intentionally omitted: the shadcn overlay `Tooltip` owns that
  // name in SCOPE. The Recharts tooltip is exposed as `ChartTooltip` instead.
  Legend: Recharts.Legend,
  ReferenceLine: Recharts.ReferenceLine,
  ReferenceArea: Recharts.ReferenceArea,
  ReferenceDot: Recharts.ReferenceDot,
  ResponsiveContainer: Recharts.ResponsiveContainer,
  // `Label` is intentionally omitted: the shadcn form `Label` owns that name in
  // SCOPE. Use `LabelList` for Recharts data labels.
  LabelList: Recharts.LabelList,
  Brush: Recharts.Brush,
  Surface: Recharts.Surface,
  Sector: Recharts.Sector
} as const

// Everything user code can reference without importing. Also re-exported
// through the require shim so explicit imports resolve to the same refs.
//
// IMPORTANT: every name here must also be listed in
// `packages/ontology/agents/src/agents/dashboard/shadcn-docs.ts` (the agent's
// authoritative component catalog). When you add/remove an entry, update both.
//
// `Tooltip` collides between Radix (shadcn) and Recharts. Shadcn `Tooltip`
// is the dashboard-overlay primitive operators expect by default; the
// Recharts tooltip is conventionally addressed as `<ChartTooltip>` inside
// `<ChartContainer>`. So we keep shadcn `Tooltip` in scope and drop
// Recharts `Tooltip` from the global name table (it's still reachable via
// `ChartTooltip` from the chart module).
const SCOPE: Record<string, unknown> = {
  // Composition primitive — instantiate a saved template at render time.
  Template,
  // Host-authored review control — placeable by agents, PATCH only on click.
  ReviewActions,
  // Canonical application href builders. Entity links must flow through
  // `plantHref` rather than model-authored route strings: with a label it
  // selects the bespoke Plant surface immediately; without one it uses the
  // universal `/plant/entity/:id` resolver.
  dashboardHref,
  dashboardTemplateHref,
  docsHubDocumentHref,
  plantHref,
  tagsTagHref,

  // P&ID / process-mimic (lattice diagram + equipment glyphs)
  AnalogIndicator,
  At,
  PidDiagram,
  PidNode,
  Pipe,
  PipePath,
  Pump,
  Sensor,
  Tank,
  Thermometer,
  U,
  Valve,

  // React
  useState: ReactNamespace.useState,
  useEffect: ReactNamespace.useEffect,
  useMemo: ReactNamespace.useMemo,
  useRef: ReactNamespace.useRef,
  useCallback: ReactNamespace.useCallback,
  useReducer: ReactNamespace.useReducer,
  useId: ReactNamespace.useId,
  useLayoutEffect: ReactNamespace.useLayoutEffect,
  Fragment: ReactNamespace.Fragment,

  // Live-data hooks (see data-runtime.ts). Each calls the proxy directly
  // using the API key the parent injected via `sandbox-init`.
  useActiveAlarms,
  useAgentFindings,
  useAgentResults,
  useAlarmStatus,
  useCypherQuery,
  useDbConnections,
  useDbQuery,
  useDbWrite,
  useGatewayStatus,
  usePowerAnalysis,
  useSoapOperation,
  useTagHistory,
  useTagWrite,
  useTagValues,

  // shadcn primitives (alphabetical)
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Alert,
  AlertDescription,
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  AlertDialogPortal,
  AlertDialogTitle,
  AlertDialogTrigger,
  AlertTitle,
  AspectRatio,
  Avatar,
  AvatarBadge,
  AvatarFallback,
  AvatarGroup,
  AvatarGroupCount,
  AvatarImage,
  Badge,
  Breadcrumb,
  BreadcrumbEllipsis,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  Button,
  ButtonGroup,
  ButtonGroupSeparator,
  ButtonGroupText,
  Calendar,
  CalendarDayButton,
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  ChartContainer,
  ChartLegend,
  ChartLegendContent,
  ChartStyle,
  ChartTooltip,
  ChartTooltipContent,
  Checkbox,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Command,
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandShortcut,
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuLabel,
  ContextMenuSeparator,
  ContextMenuTrigger,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  DrawerPortal,
  DrawerTitle,
  DrawerTrigger,
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  Field,
  FieldContent,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
  FieldLegend,
  FieldSeparator,
  FieldSet,
  FieldTitle,
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
  Input,
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupInput,
  InputGroupText,
  InputGroupTextarea,
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
  Item,
  ItemActions,
  ItemContent,
  ItemDescription,
  ItemFooter,
  ItemGroup,
  ItemHeader,
  ItemMedia,
  ItemSeparator,
  ItemTitle,
  Kbd,
  KbdGroup,
  Label,
  Menubar,
  MenubarCheckboxItem,
  MenubarContent,
  MenubarGroup,
  MenubarItem,
  MenubarLabel,
  MenubarMenu,
  MenubarPortal,
  MenubarRadioGroup,
  MenubarRadioItem,
  MenubarSeparator,
  MenubarShortcut,
  MenubarSub,
  MenubarSubContent,
  MenubarSubTrigger,
  MenubarTrigger,
  NativeSelect,
  NativeSelectOptGroup,
  NativeSelectOption,
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuIndicator,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
  NavigationMenuViewport,
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
  Popover,
  PopoverAnchor,
  PopoverContent,
  PopoverDescription,
  PopoverHeader,
  PopoverTitle,
  PopoverTrigger,
  Progress,
  RadioGroup,
  RadioGroupItem,
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
  ScrollArea,
  ScrollBar,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
  Separator,
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupAction,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInput,
  SidebarInset,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSkeleton,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarProvider,
  SidebarRail,
  SidebarSeparator,
  SidebarTrigger,
  Skeleton,
  Slider,
  Spinner,
  Switch,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Textarea,
  Toaster,
  Toggle,
  ToggleGroup,
  ToggleGroupItem,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,

  // Recharts primitives — for use inside <ChartContainer>. Tooltip is
  // intentionally NOT included here (the shadcn Tooltip occupies that
  // slot; use <ChartTooltip> for chart-specific tooltips).
  ...RECHARTS_SCOPE
}

type BabelModule = typeof import('@babel/standalone')

let babelPromise: Promise<BabelModule> | null = null
export function loadBabel(): Promise<BabelModule> {
  babelPromise ??= import('@babel/standalone').then((mod) => {
    setTemplateCompiler((code) => compileComponent(mod, code))
    return mod
  })
  return babelPromise
}

export interface CompileResult {
  /** The default-exported component, or null when compilation failed / produced no export. */
  Component: ComponentType<Record<string, unknown>> | null
  error: string | null
}

export function tagHistoryHookForCode(
  code: string
): typeof useTagHistory | typeof useLegacyTagHistory {
  return usesUtcIsoDashboardTimestamps(code) ? useTagHistory : useLegacyTagHistory
}

/**
 * Compile a TSX source string into a React component, returning
 * `{ Component: null, error }` on any failure. Single source of truth
 * for both top-level dashboards / templates AND the in-scope
 * `<Template />` primitive's lazy template resolution.
 */
export function compileComponent(babel: BabelModule, code: string): CompileResult {
  try {
    const transformed = babel.transform(code, {
      filename: 'dashboard.tsx',
      presets: [['react'], ['typescript', { isTSX: true, allExtensions: true }]],
      plugins: ['transform-modules-commonjs']
    }).code

    if (transformed === null || transformed === undefined) {
      return { Component: null, error: 'Empty output.' }
    }

    const scope: Record<string, unknown> = {
      ...SCOPE,
      useTagHistory: tagHistoryHookForCode(code)
    }
    const uiModule = { ...scope, __esModule: true }
    const requireShim = (name: string): unknown => {
      if (name === 'react') return ReactNamespace
      if (name === 'lucide-react') return { ...LucideIcons, __esModule: true }
      if (name === '@phosphor-icons/react') return { ...PhosphorIcons, __esModule: true }
      if (name === 'recharts') return { ...Recharts, __esModule: true }
      // Any `@/components/ui/*` (or relative `.../card`) path resolves to
      // the full UI module so destructured imports pick out their member.
      if (name.includes('components/ui') || name.startsWith('@/')) return uiModule
      return {}
    }

    const moduleObj: { exports: Record<string, unknown> } = { exports: {} }
    const scopeNames = Object.keys(scope)
    const scopeValues = scopeNames.map((key) => scope[key])
    // `React` is passed explicitly because the classic JSX runtime
    // compiles `<div/>` to `React.createElement(...)` and the user's code
    // has no `import React` to satisfy that free reference. The rest of
    // SCOPE is spread in as named params so unimported components resolve.
    // eslint-disable-next-line @typescript-eslint/no-implied-eval, no-new-func
    const factory = new Function(
      'require',
      'exports',
      'module',
      'React',
      ...scopeNames,
      transformed
    )
    factory(requireShim, moduleObj.exports, moduleObj, ReactNamespace, ...scopeValues)

    const exported = moduleObj.exports as { default?: unknown }
    const candidate = (exported.default ?? moduleObj.exports) as unknown
    if (typeof candidate !== 'function') {
      return { Component: null, error: 'No default-exported component.' }
    }
    return { Component: candidate as ComponentType<Record<string, unknown>>, error: null }
  } catch (err) {
    return { Component: null, error: err instanceof Error ? err.message : String(err) }
  }
}

/**
 * @deprecated Use {@link compileComponent}. Kept temporarily as an
 * alias so the sandbox host's existing call site still resolves while
 * call-sites migrate.
 */
export const compileDashboard = compileComponent
